export interface RequirementConceptInput {
  type?: string | undefined;
  text: string;
}

export interface RequirementConcept {
  key: string;
  label: string;
}

interface KnownRequirementConcept {
  key: string;
  label: string;
  typePattern: RegExp;
  textPattern: RegExp;
}

const KNOWN_REQUIREMENT_CONCEPTS: readonly KnownRequirementConcept[] = [
  {
    key: "central-control-screen",
    label: "Central control screen",
    typePattern: /central.*control.*screen|central.*screen|whole.*home.*control/i,
    textPattern:
      /\bcentral control screen\b|\bwhole[- ]home (?:control|screen)\b|\bCCS\b/i
  },
  {
    key: "bms-integration",
    label: "BMS / BACnet integration",
    typePattern: /bms|bacnet|building.*management.*integration/i,
    textPattern: /\bBMS\b|\bBACnet(?:\/IP)?\b|\bbuilding management system\b/i
  },
  {
    key: "metering",
    label: "Energy metering",
    typePattern: /meter/i,
    textPattern: /\bmeter(?:ing|s|ed)?\b|\benergy meter/i
  },
  {
    key: "leak-detection",
    label: "Leak detection",
    typePattern: /leak/i,
    textPattern: /\bleak (?:detection|detector|sensor|alarm)/i
  }
];

const GENERIC_REQUIREMENT_TYPES = new Set([
  "",
  "other",
  "requirement",
  "requirements",
  "requirementamendment",
  "projectrequirement"
]);

const normalise = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const normaliseText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "-");

const labelise = (value: string): string => {
  const spaced = value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!spaced) {
    return "Requirement";
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Resolve a conservative display-only semantic key. Structured known types
 * win; strong text aliases repair generic/missing types; unknown structured
 * types only merge with the same type. Generic unknown statements require an
 * exact normalised text match so presentation never invents a semantic merge.
 */
export function requirementConceptFor({
  type = "",
  text
}: RequirementConceptInput): RequirementConcept {
  const normalisedType = normalise(type);
  const structuredMatch = KNOWN_REQUIREMENT_CONCEPTS.find((concept) =>
    concept.typePattern.test(normalisedType)
  );

  if (structuredMatch) {
    return { key: structuredMatch.key, label: structuredMatch.label };
  }

  const textMatch = KNOWN_REQUIREMENT_CONCEPTS.find((concept) =>
    concept.textPattern.test(text)
  );

  if (textMatch) {
    return { key: textMatch.key, label: textMatch.label };
  }

  if (!GENERIC_REQUIREMENT_TYPES.has(normalisedType)) {
    return {
      key: `type-${normalisedType}`,
      label: labelise(type)
    };
  }

  const textKey = normaliseText(text) || "empty";

  return {
    key: `statement-${textKey}`,
    label: labelise(type || text.slice(0, 54))
  };
}
